import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { getAuthHeaders } from '../../../lib/api';

function ToggleRow({
  id,
  label,
  description,
  enabled,
  disabled,
  disabledReason,
  loading,
  onToggle,
}) {
  return (
    <div className={`flex items-start justify-between gap-4 py-4 ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        {disabled && disabledReason && (
          <p className="text-xs text-amber-700 mt-2">{disabledReason}</p>
        )}
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={enabled}
        disabled={disabled || loading}
        onClick={() => onToggle(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
          enabled ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export default function RelationshipTriggersPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [birthdayEnabled, setBirthdayEnabled] = useState(false);
  const [anniversaryEnabled, setAnniversaryEnabled] = useState(false);
  const [birthdayCount, setBirthdayCount] = useState(0);
  const [anniversaryCount, setAnniversaryCount] = useState(0);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/relationship-triggers-status', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setBirthdayEnabled(data.birthdayTriggersEnabled);
      setAnniversaryEnabled(data.homeAnniversaryTriggersEnabled);
      setBirthdayCount(data.birthdayClientCount);
      setAnniversaryCount(data.homeAnniversaryClientCount);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const saveToggle = async (field, value) => {
    setSaving(true);
    setError(null);
    const prevBirthday = birthdayEnabled;
    const prevAnniversary = anniversaryEnabled;
    if (field === 'birthday') setBirthdayEnabled(value);
    else setAnniversaryEnabled(value);

    try {
      const headers = await getAuthHeaders();
      const body =
        field === 'birthday'
          ? { birthdayTriggersEnabled: value }
          : { homeAnniversaryTriggersEnabled: value };
      const res = await fetch('/api/user/relationship-triggers-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
    } catch (err) {
      setBirthdayEnabled(prevBirthday);
      setAnniversaryEnabled(prevAnniversary);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-10 border border-border rounded-xl bg-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Heart" size={22} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Relationship Triggers</h2>
            <p className="text-sm text-muted-foreground">
              Automatic birthday and home anniversary texts — only after a contact has received their
              first-touch intro
            </p>
          </div>
        </div>
      </div>

      <div className="px-6">
        {loading ? (
          <p className="py-6 text-sm text-muted-foreground flex items-center gap-2">
            <Icon name="Loader2" size={16} className="animate-spin" />
            Loading...
          </p>
        ) : (
          <>
            {error && <p className="py-3 text-sm text-destructive">{error}</p>}
            <ToggleRow
              id="birthday-triggers"
              label="Birthday"
              description="Send a warm birthday message on each client's birthday (only after their first-touch intro)."
              enabled={birthdayEnabled}
              disabled={birthdayCount === 0}
              disabledReason={
                birthdayCount === 0
                  ? 'Add birthdays on your clients (Client Management) to enable.'
                  : null
              }
              loading={saving}
              onToggle={(v) => saveToggle('birthday', v)}
            />
            <div className="border-t border-border" />
            <ToggleRow
              id="anniversary-triggers"
              label="Home anniversary"
              description="Reach out on the anniversary of their home closing (only after their first-touch intro)."
              enabled={anniversaryEnabled}
              disabled={anniversaryCount === 0}
              disabledReason={
                anniversaryCount === 0
                  ? 'Add home closing dates on your clients to enable.'
                  : null
              }
              loading={saving}
              onToggle={(v) => saveToggle('anniversary', v)}
            />
            {(birthdayCount > 0 || anniversaryCount > 0) && (
              <p className="pb-4 text-xs text-muted-foreground">
                {birthdayCount} client{birthdayCount !== 1 ? 's' : ''} with birthdays,{' '}
                {anniversaryCount} with home closing dates
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

